// 展厅周围随机种树、种草（都用 InstancedMesh，随机角度 + 随机大小）
import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'

// 程序生成"一丛草"的贴图（带透明通道，用来做草叶面片）
export function makeTuftTexture(size = 256) {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const ctx = c.getContext('2d')
  ctx.clearRect(0, 0, size, size)
  const rnd = (() => {
    let a = 20260917
    return () => { a = (a * 1664525 + 1013904223) % 4294967296; return a / 4294967296 }
  })()
  const blades = 26
  for (let i = 0; i < blades; i++) {
    const x0 = size * (0.18 + rnd() * 0.64)
    const h = size * (0.45 + rnd() * 0.5)
    const bend = (rnd() - 0.5) * size * 0.34
    const w = size * (0.012 + rnd() * 0.016)
    const g = 110 + rnd() * 90
    ctx.strokeStyle = `rgba(${Math.round(g * 0.45)},${Math.round(g)},${Math.round(g * 0.35)},${(0.75 + rnd() * 0.25).toFixed(2)})`
    ctx.lineWidth = w
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(x0, size)
    ctx.quadraticCurveTo(x0 + bend * 0.4, size - h * 0.55, x0 + bend, size - h)
    ctx.stroke()
  }
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = THREE.SRGBColorSpace
  return t
}

function loadTex(url, srgb, aniso) {
  const t = new THREE.TextureLoader().load(url)
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  t.anisotropy = aniso || 4
  return t
}

/**
 * 创建植被。
 * @param {object} opts
 *   center   {x,z} 圆心（和无限地面同一中心）
 *   groundY  地面高度
 *   isBlocked(x,z) 返回 true 的位置不种东西（例如展厅本身）
 */
export function createVegetation({ center, groundY, isBlocked, maxAniso = 8 }) {
  const group = new THREE.Group()
  group.name = 'Vegetation'

  const state = {
    trees: { on: true, seed: 12345, count: 140, minR: 95, maxR: 430, scaleMin: 0.85, scaleMax: 1.6 },
    grass: { on: true, count: 3600, minR: 85, maxR: 460, scaleMin: 0.6, scaleMax: 1.5 },
    shrubs: { on: true, count: 400, minR: 85, maxR: 460, scaleMin: 0.6, scaleMax: 1.6, seed: 12345 },
  }

  // 矮树/灌木：加载 searsia_lucida 的 7 种变体，每种一个 InstancedMesh
  const shrubMeshes = []
  const shrubTris = []
  function loadShrubs() {
    console.log('[vegetation] 开始加载矮树模型 /models/shrubs.glb')
    const loader = new GLTFLoader()
    const draco = new DRACOLoader()
    draco.setDecoderPath('/draco/')
    loader.setDRACOLoader(draco)
    loader.load('/models/shrubs.glb', (gltf) => {
      gltf.scene.updateMatrixWorld(true)
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return
        const geo = o.geometry.clone()
        geo.applyMatrix4(o.matrixWorld)
        const mat = o.material && o.material.map ? o.material.clone()
          : new THREE.MeshStandardMaterial({ color: 0x4a6b33 })
        mat.side = THREE.DoubleSide
        if (mat.map) mat.map.anisotropy = maxAniso
        // 矮树（searsia lucida）：树干是 OPAQUE、叶片是 BLEND（贴图自带 alpha），
        // 直接沿用 glTF 的设置最贴近 Blender 里的样子；只额外打开 depthWrite 减少排序闪烁。
        if (mat.transparent) {
          mat.depthWrite = true
          mat.alphaTest = 0
        } else {
          mat.opacity = 1
          mat.alphaTest = 0
        }
        mat.shadowSide = THREE.DoubleSide
        const inst = new THREE.InstancedMesh(geo, mat, 900)
        inst.name = 'ShrubInstances'
        inst.frustumCulled = false
        inst.count = 0
        shrubMeshes.push(inst)
        group.add(inst)
      })
      console.log('[vegetation] 矮树变体', shrubMeshes.length, '个')
      rebuild()
      apply()
    }, undefined, (err) => console.warn('[vegetation] 矮树加载失败', err))
  }
  loadShrubs()

  const tuftTex = makeTuftTexture(256)
  tuftTex.anisotropy = maxAniso

  const CAP_TREE = 3000
  const CAP_GRASS = 20000
  // 立体树：fir_tree_01 的 a/b/c 三个变体（真实几何，不是照片切片）
  const treeMeshes = []
  const treeVariantKeys = []   // 与 treeMeshes 一一对应，用于按变体启用/停用
  function loadFirTrees() {
    const loader = new GLTFLoader()
    const draco = new DRACOLoader()
    draco.setDecoderPath('/draco/')
    loader.setDRACOLoader(draco)
    loader.load('/models/fir.glb', (gltf) => {
      gltf.scene.updateMatrixWorld(true)
      // 关键：一棵树的"树干+树冠"是多个网格，必须当成一个整体——同一实例要有相同的变换矩阵，
      // 否则会出现"只有树干"或"只有树冠"（看着像脱开）
      const byVariant = new Map()
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return
        let root = o
        while (root.parent && root.parent !== gltf.scene) root = root.parent
        const key = root.name || 'variant'
        if (!byVariant.has(key)) byVariant.set(key, [])
        byVariant.get(key).push(o)
      })
      byVariant.forEach((meshes, key) => {
        const parts = meshes.map((o) => {
          const geo = o.geometry.clone()
          geo.applyMatrix4(o.matrixWorld)
          const mat = o.material ? o.material.clone() : new THREE.MeshStandardMaterial({ color: 0x2f4a25 })
          mat.side = THREE.DoubleSide
          // 枝叶材质用 alpha 镂空（MASK），树干/树皮保持不透明：
          // 这样既不会"有枝无干"，针叶的边缘也保留镂空细节
          const nm = String(mat.name || '').toLowerCase()
          const leafy = /needle|leaf|leaves|branch|twig|foliage|leafy/.test(nm)
          mat.transparent = false
          mat.depthWrite = true
          mat.opacity = 1
          mat.alphaTest = leafy ? 0.5 : 0
          // 枝叶很薄，单面投影会漏光 → 双面投影，树冠的影子才会出来
          mat.shadowSide = THREE.DoubleSide
          // 19 米那个变体（fir_tree_01_a）的颜色贴图里烘了较重的 AO，两侧发黑，这里单独提亮
          if (/_a_|_a\.|a_LOD/i.test(key)) {
            mat.color.multiplyScalar(1.3)
            if ('aoMap' in mat) mat.aoMap = null
            mat.needsUpdate = true
          }
          const inst = new THREE.InstancedMesh(geo, mat, 900)
          inst.name = 'TreeMesh'
          inst.frustumCulled = false
          inst.count = 0
          inst.castShadow = true
          inst.receiveShadow = true
          group.add(inst)
          return inst
        })
        // 树冠"投影代理"：一个看不见的椭球，只用来投影。
        // 针叶是细碎几何，直接投影会很稀疏、面积也比树冠小；用代理就能得到一整片树冠形状的影子。
        try {
          const box = new THREE.Box3()
          meshes.forEach((o) => box.expandByObject(o))
          const size = box.getSize(new THREE.Vector3())
          const ctr = box.getCenter(new THREE.Vector3())
          const crownH = size.y * 0.66
          const crownY = box.min.y + size.y * 0.62
          const proxyGeo = new THREE.SphereGeometry(0.5, 12, 8)
          proxyGeo.scale(size.x * 0.5, crownH * 0.5, size.z * 0.5)
          proxyGeo.translate(ctr.x, crownY, ctr.z)
          // 用「全透明」而不是 colorWrite:false —— 后者在部分 three 版本里连阴影 pass 一起跳过
          const proxyMat = new THREE.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: 0,
            depthWrite: false,
          })
          const proxy = new THREE.InstancedMesh(proxyGeo, proxyMat, 900)
          proxy.name = 'TreeShadowProxy'
          proxy.frustumCulled = false
          proxy.castShadow = true      // 只投影
          proxy.receiveShadow = false
          proxy.renderOrder = -1
          group.add(proxy)
          parts.push(proxy)
        } catch (e) { console.warn('[vegetation] 代理生成失败', e) }
        treeMeshes.push(parts)
        treeVariantKeys.push(key)
        console.log('[vegetation] 树变体', key, '含', parts.length, '个网格', meshes.map((m) => m.material && m.material.name).join(','))
      })
      console.log('[vegetation] 立体树变体', treeMeshes.length, '组')
      rebuild()
      apply()
    }, undefined, (err) => console.warn('[vegetation] 立体树加载失败', err))
  }
  loadFirTrees()
  const grassMesh = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshStandardMaterial({
      map: tuftTex, transparent: true, alphaTest: 0.4, side: THREE.DoubleSide,
      roughness: 1, metalness: 0,
    }),
    CAP_GRASS
  )
  grassMesh.name = 'GrassTufts'
  grassMesh.frustumCulled = false
grassMesh.castShadow = true
grassMesh.receiveShadow = true

  group.add(grassMesh)   // 树的实例在加载完成时已经加进 group 了

  // 共享随机数：每次 rebuild 都从同一个种子重新开始 → 只改矮树参数不会把大树重新洗牌
  let rndState = 987654321
  const rnd = () => {
    rndState = (rndState * 1103515245 + 12345) % 2147483648
    return rndState / 2147483648
  }

  const m4 = new THREE.Matrix4()
  const q = new THREE.Quaternion()
  const pos = new THREE.Vector3()
  const scl = new THREE.Vector3()
  const up = new THREE.Vector3(0, 1, 0)

  function place(mesh, slot, x, y, z, w, h, rotY, tint) {
    pos.set(x, y + h / 2, z)
    q.setFromAxisAngle(up, rotY)
    scl.set(w, h, 1)
    m4.compose(pos, q, scl)
    mesh.setMatrixAt(slot, m4)
    if (tint && mesh.instanceColor) mesh.setColorAt(slot, tint)
  }

  function rebuild() {
    rndState = (Number(state.trees.seed) | 0) || 987654321
    const c = new THREE.Color()
    // ---- 立体树：一棵一个实例（真实几何，模型自带 14~19 米高）----
    if (treeMeshes.length) {
      for (const parts of treeMeshes) for (const m of parts) m.count = 0
      // 只保留启用的变体（对应 a/b/c 三个勾选框）
      const enabled = state.trees.enabled || [true, true, true]
      for (let i = 0; i < state.trees.count; i++) {
        let x = 0
        let z = 0
        let ok = false
        for (let tries = 0; tries < 12 && !ok; tries++) {
          const a = rnd() * Math.PI * 2
          const r = state.trees.minR + rnd() * Math.max(1, state.trees.maxR - state.trees.minR)
          x = center.x + Math.cos(a) * r
          z = center.z + Math.sin(a) * r
          ok = !(isBlocked && isBlocked(x, z))
        }
        if (!ok) continue
        const pool = treeMeshes.filter((p, k) => enabled[k] !== false)
        if (!pool.length) break
        const parts = pool[i % pool.length]
        if (!parts.length || parts[0].count >= 900) continue
        const sc = state.trees.scaleMin + rnd() * (state.trees.scaleMax - state.trees.scaleMin)
        pos.set(x, groundY, z)
        q.setFromEuler(new THREE.Euler((rnd() - 0.5) * 0.08, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.08))
        scl.set(sc, sc * (0.92 + rnd() * 0.18), sc)
        m4.compose(pos, q, scl)
        // 树干、树冠用同一个矩阵 → 永远贴合在一起
        for (const m of parts) { m.setMatrixAt(m.count, m4); m.count++ }
      }
      for (const parts of treeMeshes) for (const m of parts) m.instanceMatrix.needsUpdate = true
    }

    // ---- 草：每丛 2 张交叉面片 ----
    let gi = 0
    for (let i = 0; i < state.grass.count * 2; i++) {
      if (gi >= CAP_GRASS) break
      let x = 0
      let z = 0
      let ok = false
      for (let tries = 0; tries < 10 && !ok; tries++) {
        const a = rnd() * Math.PI * 2
        const r = state.grass.minR + rnd() * Math.max(1, state.grass.maxR - state.grass.minR)
        x = center.x + Math.cos(a) * r
        z = center.z + Math.sin(a) * r
        ok = !(isBlocked && isBlocked(x, z))
      }
      if (!ok) continue
      const scale = state.grass.scaleMin + rnd() * (state.grass.scaleMax - state.grass.scaleMin)
      const h = 1.15 * scale
      const w = h * 1.1
      c.setHSL(0.26 + (rnd() - 0.5) * 0.06, 0.35 + rnd() * 0.25, 0.38 + rnd() * 0.2)
      place(grassMesh, gi, x, groundY + 0.01, z, w, h, rnd() * Math.PI, c)
      gi++
      if (gi < CAP_GRASS) {
        place(grassMesh, gi, x, groundY + 0.01, z, w, h, rnd() * Math.PI + Math.PI / 2, c)
        gi++
      }
    }
    grassMesh.count = gi
    grassMesh.instanceMatrix.needsUpdate = true
    if (grassMesh.instanceColor) grassMesh.instanceColor.needsUpdate = true
    // ---- 矮树/灌木（7 种变体随机取，避免展厅、贴地、随机旋转/大小/倾斜）----
    if (shrubMeshes.length) {
      const s = state.shrubs
      const rnd2 = (() => { let a = (s.seed | 1); return () => { a = (a * 48271) % 2147483647; return a / 2147483647 } })()
      shrubMeshes.forEach((m) => { m.count = 0 })
      const shrubOn = state.shrubs.enabled || []
      const perMesh = Math.max(1, Math.ceil(s.count / shrubMeshes.length))
      const shrubPool = shrubMeshes.map((m, k) => k).filter((k) => shrubOn[k] !== false)
      for (let i = 0; i < s.count; i++) {
        if (!shrubPool.length) break
        let x = 0, z = 0, ok = false
        for (let tries = 0; tries < 12 && !ok; tries++) {
          const a = rnd2() * Math.PI * 2
          const r = s.minR + rnd2() * Math.max(1, s.maxR - s.minR)
          x = center.x + Math.cos(a) * r
          z = center.z + Math.sin(a) * r
          ok = !(isBlocked && isBlocked(x, z))
        }
        if (!ok) continue
        const mesh = shrubMeshes[shrubPool[i % shrubPool.length]]
        if (mesh.count >= 900) continue
        const sc = s.scaleMin + rnd2() * (s.scaleMax - s.scaleMin)
        pos.set(x, groundY, z)
        q.setFromEuler(new THREE.Euler((rnd2() - 0.5) * 0.12, rnd2() * Math.PI * 2, (rnd2() - 0.5) * 0.12))
        scl.set(sc, sc * (0.9 + rnd2() * 0.25), sc)
        m4.compose(pos, q, scl)
        mesh.setMatrixAt(mesh.count, m4)
        mesh.count++
      }
      shrubMeshes.forEach((m) => {
        m.instanceMatrix.needsUpdate = true
        m.visible = !!s.on && m.count > 0
      })
      console.log('[vegetation] 矮树', shrubMeshes.reduce((n, m) => n + m.count, 0), '棵')
    }
    console.log('[vegetation] 树', treeMeshes.reduce((n, parts) => n + (parts[0] ? parts[0].count : 0), 0), '棵 / 草', gi, '张面片')
  }

  function apply() {
    for (const parts of treeMeshes) for (const m of parts) m.visible = !!state.trees.on && m.count > 0
    grassMesh.visible = !!state.grass.on && grassMesh.count > 0
    shrubMeshes.forEach((m) => { m.visible = !!state.shrubs.on && m.count > 0 })
  }

  function setVisible(on) {
    group.visible = !!on
  }

  return { group, state, rebuild: () => { rebuild(); apply() }, apply, setVisible }
}






